import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Library, FolderLock, Loader2, Save, X, Check } from "lucide-react";
import { uploadMedia, publicUrl, fetchAlbums } from "@/components/gallery/galleryApi";
import { streamAsset, libraryApi, mistApi } from "@/api/mist";
import { useSession } from "@/context/SessionContext";

// Universal save panel for EVERY AI generation tool.
//   • Download  — device-only download (no side effects).
//   • Save…     — opens a popup with a required filename + toggles:
//        · Mist Library (ALL users → personal library)
//        · Stock Library (FOUNDER only → public Misteck Vault album)
// Provide EITHER `assetId` (Mist asset, resolved via streamAsset) OR a direct `url`.
// `commit` = the assetId is a freshly-generated STAGED asset → "Save to Mist
//   Library" COMMITS (unstages) it in place instead of creating a duplicate copy.
const ALBUM_BY_TRACK = { IMAGES: "Mist Creations", VIDEOS: "Mist Reels", AUDIO: "Mist Audio" };
const EXT_BY_TRACK = { IMAGES: "png", VIDEOS: "mp4", AUDIO: "mp3" };

export default function SaveDestinations({ assetId, url, track = "IMAGES", filename = "mist_creation", ext, album, commit = false, className = "", size = "md" }) {
  const { user } = useSession();
  const isFounder = user?.role === "founder";
  const fileExt = ext || EXT_BY_TRACK[track] || "bin";

  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [toDevice, setToDevice] = useState(true);
  const [toMist, setToMist] = useState(false);
  const [toStock, setToStock] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [albumsFetched, setAlbumsFetched] = useState(false);
  const [stockAlbum, setStockAlbum] = useState(album || "");

  const defaultName = (filename || "mist_creation").replace(/\.[^.]+$/, "");
  const safeKey = (name || defaultName).replace(/[^a-z0-9]+/gi, "_").toLowerCase().replace(/^_+|_+$/g, "") || "mist_creation";

  useEffect(() => {
    if (open) setName((prev) => prev || defaultName);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && isFounder && toStock && albums.length === 0) {
      fetchAlbums(track).then((rows) => {
        const list = (rows || []).map((a) => a.name).filter(Boolean);
        setAlbums(list);
        setAlbumsFetched(true);
        if (!stockAlbum) setStockAlbum(album || list[0] || ALBUM_BY_TRACK[track]);
      }).catch(() => setAlbumsFetched(true));
    }
  }, [open, isFounder, toStock]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveBlob = async () => {
    let src;
    if (url) src = /^(https?:|blob:|data:)/i.test(url) ? url : publicUrl(url);
    else if (assetId) src = await streamAsset(assetId);
    else throw new Error("No asset to save");
    return await (await fetch(src)).blob();
  };

  const downloadBlob = (blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${safeKey}.${fileExt}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const doSave = async () => {
    const nm = (name || "").trim();
    if (!nm) { toast.error("Please enter a file name"); return; }
    if (!toDevice && !toMist && !toStock) { toast.error("Pick at least one destination"); return; }
    if (toStock && !stockAlbum) { toast.error("Pick a Community album"); return; }
    setBusy(true);
    const t = toast.loading("Saving…");
    try {
      const blob = await resolveBlob();
      const file = new File([blob], `${safeKey}.${fileExt}`, { type: blob.type || undefined });
      const done = [];
      if (toDevice) { downloadBlob(blob); done.push("Device"); }
      if (toMist) {
        if (commit && assetId) {
          // Freshly-generated staged asset → rename + unstage in place (no duplicate).
          try { await mistApi.renameAsset(assetId, nm); } catch (_) { /* name best-effort */ }
          await mistApi.commitAsset(assetId);
        } else {
          await libraryApi.saveBlob({ file, name: nm, track });
        }
        done.push("Mist Library");
      }
      if (toStock && isFounder) {
        await uploadMedia({ file, key: nm, category: stockAlbum, track, destination: "public", ext: fileExt });
        done.push("Community");
      }
      toast.success(`Saved to ${done.join(" + ")} ✓`, { id: t });
      setOpen(false);
    } catch (e) { toast.error(e.message || "Save failed", { id: t }); }
    finally { setBusy(false); }
  };

  const pad = size === "sm" ? "px-3 py-2 text-xs" : "px-3.5 py-2.5 text-sm";

  return (
    <>
      <div className={className} data-testid="save-destinations">
        <button type="button" data-testid="save-dest-open" onClick={() => setOpen(true)} disabled={busy}
          className={`w-full flex items-center justify-center gap-2 border-2 border-black bg-black text-[#00F5D4] font-serif shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 ${pad}`}>
          <Save size={15} />Save…
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3"
          data-testid="save-popup" onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}>
          <div className="w-full max-w-sm border-2 border-[#00F5D4]/40 bg-[#0a0a0a] shadow-[6px_6px_0px_#000] p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-base text-white tracking-wide">Save creation</h3>
              <button type="button" data-testid="save-popup-close" onClick={() => !busy && setOpen(false)}
                className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-widest text-white/50">File name</label>
              <input data-testid="save-popup-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name" autoFocus
                className="w-full bg-black border-2 border-white/15 focus:border-[#00F5D4]/60 outline-none px-3 py-2.5 text-sm text-white font-serif" />
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-white/50">Save to</label>

              <button type="button" data-testid="save-popup-toggle-device" onClick={() => setToDevice((v) => !v)}
                className={`w-full flex items-center gap-3 border-2 px-3 py-2.5 text-left transition-colors ${toDevice ? "border-[#FFD028] bg-[#FFD028]/10" : "border-white/15 bg-black"}`}>
                <span className={`w-5 h-5 flex items-center justify-center border-2 ${toDevice ? "border-[#FFD028] bg-[#FFD028]" : "border-white/30"}`}>
                  {toDevice && <Check size={13} className="text-black" />}
                </span>
                <Download size={16} className="text-[#FFD028]" />
                <span className="flex-1">
                  <span className="block text-sm font-serif text-white">Device</span>
                  <span className="block text-[11px] text-white/45">Download to this device</span>
                </span>
              </button>

              {isFounder && track !== "VIDEOS" && (
                <button type="button" data-testid="save-popup-toggle-stock" onClick={() => setToStock((v) => !v)}
                  className={`w-full flex items-center gap-3 border-2 px-3 py-2.5 text-left transition-colors ${toStock ? "border-[#00F5D4] bg-[#00F5D4]/10" : "border-white/15 bg-black"}`}>
                  <span className={`w-5 h-5 flex items-center justify-center border-2 ${toStock ? "border-[#00F5D4] bg-[#00F5D4]" : "border-white/30"}`}>
                    {toStock && <Check size={13} className="text-black" />}
                  </span>
                  <Library size={16} className="text-[#00F5D4]" />
                  <span className="flex-1">
                    <span className="block text-sm font-serif text-white">Community Share</span>
                    <span className="block text-[11px] text-white/45">Share with the Community</span>
                  </span>
                </button>
              )}

              {isFounder && toStock && (
                <div className="pl-1 space-y-1.5" data-testid="save-popup-album-wrap">
                  <label className="text-[11px] uppercase tracking-widest text-white/40">Community album</label>
                  <select data-testid="save-popup-album" value={stockAlbum} onChange={(e) => setStockAlbum(e.target.value)}
                    className="w-full bg-black border-2 border-white/15 focus:border-[#00F5D4]/60 outline-none px-3 py-2 text-sm text-white font-serif">
                    {albums.length === 0 && <option value="">{albumsFetched ? "No albums yet" : "Loading albums…"}</option>}
                    {albums.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              )}
            </div>

            <button type="button" data-testid="save-popup-confirm" onClick={doSave} disabled={busy}
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#00F5D4] text-black font-serif py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save
            </button>
          </div>
        </div>
      )}
    </>
  );
}
